import { cva } from 'class-variance-authority'
import { Switch as AriaSwitch, type SwitchProps as AriaSwitchProps } from 'react-aria-components'
import { cn } from '../../lib/cn'
import { focusRing } from '../../lib/focus-ring'

/**
 * The Toggle set (463:56543, also drawn on the Creative canvas as 271:3312):
 * Status Switch = Off / On. The Figma property is the switch's own state, so it
 * maps to react-aria's `isSelected` (`data-selected` on the root) rather than a
 * prop of its own.
 *
 * Keyboard focus takes the kit's accent outline, which is not drawn in the Figma.
 */
export const toggleTrackVariants = cva(
	'flex h-5 w-9 shrink-0 rounded-atlas-full bg-atlas-gray-300 p-0.5 group-data-selected:bg-atlas-accent',
)

export const toggleThumbVariants = cva(
	'size-4 rounded-atlas-full bg-atlas-light shadow-atlas-drop-2 group-data-selected:translate-x-4',
)

export interface ToggleProps extends Omit<AriaSwitchProps, 'className' | 'children'> {
	className?: string
}

/**
 * A 36x20 on/off switch, built on react-aria's Switch. The Figma draws no label,
 * so give it an accessible name with `aria-label` or `aria-labelledby`.
 */
export function Toggle({ className, ...props }: ToggleProps) {
	return (
		<AriaSwitch
			{...props}
			className={cn(
				'group inline-flex cursor-pointer rounded-atlas-full data-disabled:cursor-default',
				focusRing,
				className,
			)}
		>
			<span className={toggleTrackVariants()}>
				<span className={toggleThumbVariants()} />
			</span>
		</AriaSwitch>
	)
}
